import { useId, useMemo } from 'react'
import { Badge, Button, FormSection, Tooltip } from '../common'
import { regulationSelection, sortRegulationsByLabel } from '../../utils/regulationLabels'
import styles from './PostComposer.module.css'

// Where a post runs is a distribution decision, so it belongs beside the other
// publish controls rather than inside the optional bet disclosure it used to
// live in. This component is presentational: PostCreator owns the selection so
// the default set is applied even when the operator never opens this step.
// Regulations are listed alphabetically by name whatever order they arrive in,
// so every consumer gets the same scannable list.
//
// `unrestrictedWhenEmpty` is the one thing the two consumers genuinely disagree
// on. For a single-book post an empty target set is not "nowhere": the feed
// serving predicate reads it as unrestricted. A Command Center destination
// gets no such reading -- preflight rejects a book with no regulations
// (regulation_required in post_group_v2_validation.go) -- so telling that
// operator the post "runs in every regulation" would be actively wrong.
//
// `noticeByOfferingId` ({ label, tone, description }) flags regulations that
// cannot carry the market. Unlike a disabled reason it never locks the
// checkbox: the operator decides what to drop.
export default function RegulationTargetSection({
  regulations = [],
  selected = [],
  disabledReasonByOfferingId = {},
  noticeByOfferingId = {},
  onToggle,
  onSelectAll,
  onClear,
  disabled = false,
  unrestrictedWhenEmpty = true,
}) {
  const noticeIdPrefix = useId()
  const sortedRegulations = useMemo(() => sortRegulationsByLabel(regulations), [regulations])
  if (sortedRegulations.length === 0) return null

  const selectable = sortedRegulations.filter((regulation) => !regulation.disabled)
  const { summary } = regulationSelection(selected.length, selectable.length, {
    noneLabel: unrestrictedWhenEmpty ? 'Every regulation' : 'None selected',
  })

  return (
    <FormSection title="Where it runs" headingSize="subsection">
      <div className={styles.regulationTargetHeader}>
        <span className={styles.regulationTargetSummary}>{summary}</span>
        <div className={styles.regulationTargetActions}>
          <Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={onSelectAll}>
            Select all
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={onClear}>
            Clear
          </Button>
        </div>
      </div>
      {selected.length === 0 ? (
        <p className={styles.regulationTargetWarning} role="status">
          {unrestrictedWhenEmpty
            ? 'With nothing selected this post runs in every regulation, including any added later. Pick regulations to limit it.'
            : 'Pick at least one regulation for this book. A book with none cannot be published.'}
        </p>
      ) : null}
      <ul className={styles.regulationTargetList}>
        {sortedRegulations.map((regulation, index) => {
          const reason = disabledReasonByOfferingId[regulation.value]
          const notice = noticeByOfferingId[regulation.value]
          const noticeId = notice ? `${noticeIdPrefix}-notice-${index}` : undefined
          const checkbox = (
            <label className={styles.regulationTargetOption}>
              <input
                type="checkbox"
                checked={selected.includes(regulation.value)}
                disabled={disabled || regulation.disabled}
                aria-describedby={noticeId}
                onChange={() => onToggle(regulation.value)}
              />
              <span>{regulation.label}</span>
            </label>
          )
          return (
            <li key={regulation.value} className={styles.regulationTargetItem}>
              {reason ? <Tooltip content={reason}>{checkbox}</Tooltip> : checkbox}
              {notice ? (
                <span id={noticeId} className={styles.regulationTargetNotice}>
                  <Badge tone={notice.tone} compact title={notice.description}>{notice.label}</Badge>
                </span>
              ) : null}
            </li>
          )
        })}
      </ul>
    </FormSection>
  )
}
